import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, Check, ShieldAlert, Loader2, Download, ArrowRight, Timer, XCircle, Ban } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import { GuideShot } from "@/components/GuideShot";
import { PurchasesShot } from "@/components/PurchasesShot";

const SHOT = {
  settings: { img: "/assets/guide/settings.jpg", box: { top: "12%", left: "5%", width: "90%", height: "15%" } },
  manual: { img: "/assets/guide/manual.webp", box: { top: "71%", left: "6%", width: "88%", height: "13%" } },
  other: { img: "/assets/guide/other-options.jpg", box: { top: "89.5%", left: "9%", width: "82%", height: "6.5%" } },
  dont: { img: "/assets/guide/dont-improve.jpg", box: { top: "62.5%", left: "18%", width: "64%", height: "6%" } },
  cont: { img: "/assets/guide/continue-wipe.jpg", box: { top: "82%", left: "8%", width: "84%", height: "7%" } },
};

const fmt = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

function CopyField({ label, value, testId }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* ignore */
    }
    setDone(true);
    toast.success(`${label} скопирован`);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-sm font-mono break-all">{value}</code>
        <button
          data-testid={testId}
          onClick={copy}
          className="shrink-0 w-9 h-9 grid place-items-center rounded-xl bg-blue-500/15 text-blue-300 border border-blue-500/30 active:scale-95 transition"
        >
          {done ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>
    </div>
  );
}

function Countdown({ remainingMs }) {
  const warn = remainingMs <= 60000;
  const danger = remainingMs <= 15000;
  const tone = danger
    ? "bg-red-500/15 border-red-500/40 text-red-300"
    : warn
    ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
    : "bg-emerald-500/10 border-emerald-500/30 text-emerald-200";
  return (
    <div
      data-testid="issue-timer"
      className={`rounded-2xl border p-4 flex items-center gap-3 transition-colors ${tone}`}
    >
      <Timer size={22} className={danger ? "animate-pulse" : ""} />
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wider font-bold opacity-80">Осталось времени</div>
        <div className="text-xs opacity-80">У вас 10 минут, чтобы войти и установить нужное приложение</div>
      </div>
      <div data-testid="issue-timer-value" className="text-3xl font-mono font-bold tabular-nums">
        {fmt(remainingMs)}
      </div>
    </div>
  );
}

export default function SharedAccountModal({ open, onOpenChange, appName }) {
  const [step, setStep] = useState("logout"); // logout | login | account
  const [loading, setLoading] = useState(false);
  const [acc, setAcc] = useState(null);
  const [error, setError] = useState("");
  const [revoked, setRevoked] = useState(null); // null | { voluntary: bool }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const cancelledRef = useRef(false);
  const ridRef = useRef(null);
  const expiresRef = useRef(0); // локальное время (ms) окончания 10 минут
  const verifyStartRef = useRef(0); // локальное время (ms) старта проверки аккаунта

  useEffect(() => {
    if (open) {
      cancelledRef.current = false;
      ridRef.current = null;
      expiresRef.current = 0;
      verifyStartRef.current = 0;
      setStep("logout");
      setAcc(null);
      setError("");
      setRevoked(null);
      setConfirmOpen(false);
      setRevoking(false);
      setLoading(false);
    }
    return () => {
      cancelledRef.current = true;
    };
  }, [open]);

  // Секундный тик для таймера/обратного отсчёта.
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Автоотзыв по истечении 10 минут (локальный таймер синхронизирован с сервером).
  useEffect(() => {
    if (acc && !revoked && expiresRef.current && nowTick >= expiresRef.current) {
      setRevoked({ voluntary: false });
      track("expired", "Время 10 минут истекло", { app: appName || "" });
    }
  }, [nowTick, acc, revoked, appName]);

  // Фоновый опрос статуса (ловим отзыв со стороны сервера/бота).
  useEffect(() => {
    if (!acc || revoked || !ridRef.current) return undefined;
    const t = setInterval(async () => {
      try {
        const d = await api.issueStatus(ridRef.current);
        if (d.status === "revoked") setRevoked({ voluntary: !!d.voluntary });
      } catch {
        /* ignore */
      }
    }, 5000);
    return () => clearInterval(t);
  }, [acc, revoked]);

  const applyTiming = (data) => {
    // Переводим серверное время окончания в локальные часы (устойчиво к сдвигу часов).
    const exp = Date.parse(data.expires_at);
    const srv = Date.parse(data.server_now);
    if (!Number.isNaN(exp) && !Number.isNaN(srv)) {
      expiresRef.current = Date.now() + (exp - srv);
    }
  };

  const pollStatus = async (rid) => {
    const deadline = Date.now() + 90000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2000));
      if (cancelledRef.current) return;
      let data;
      try {
        data = await api.issueStatus(rid);
      } catch {
        continue;
      }
      if (cancelledRef.current) return;
      if (data.status === "ok") {
        applyTiming(data);
        setAcc(data);
        setLoading(false);
        track("issue_ok", "Получил аккаунт", { app: appName || "" });
        return;
      }
      if (data.status === "revoked") {
        applyTiming(data);
        setRevoked({ voluntary: !!data.voluntary });
        setLoading(false);
        return;
      }
      if (data.status === "failed") {
        track("issue_failed", data.detail || "Выдача не удалась", { app: appName || "" });
        setError(data.detail || "Не удалось подобрать рабочий аккаунт. Попробуйте ещё раз.");
        setLoading(false);
        return;
      }
    }
    track("issue_failed", "Проверка заняла слишком долго", { app: appName || "" });
    setError("Проверка заняла слишком долго. Попробуйте ещё раз.");
    setLoading(false);
  };

  const startIssue = async () => {
    if (loading) return; // защита от повторного клика / «назад → ещё раз», пока запрос в полёте
    setStep("account");
    setLoading(true);
    setError("");
    setAcc(null);
    setRevoked(null);
    setConfirmOpen(false);
    verifyStartRef.current = Date.now();
    try {
      track("issue_request", "Запросил данные аккаунта", { app: appName || "" });
      const { request_id } = await api.issueAccount();
      ridRef.current = request_id;
      await pollStatus(request_id);
    } catch (e) {
      const message = e.response?.status === 403
        ? "Получение аккаунтов для вашего аккаунта ограничено. Обратитесь к администратору"
        : e.response?.data?.detail || "Не удалось получить аккаунт";
      track("issue_failed", message, { app: appName || "" });
      setError(message);
      setLoading(false);
    }
  };

  const doRevoke = async () => {
    if (revoking || !ridRef.current) return;
    setRevoking(true);
    try {
      await api.issueRevoke(ridRef.current);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось отправить запрос на отзыв. Попробуйте ещё раз.");
      setRevoking(false);
      return;
    }
    setConfirmOpen(false);
    setRevoking(false);
    setRevoked({ voluntary: true });
    track("revoked", "Отозвал аккаунт", { app: appName || "" });
  };

  const remaining = expiresRef.current ? expiresRef.current - nowTick : 0;
  const reissueMs = expiresRef.current ? expiresRef.current - nowTick : 0;
  const VERIFY_SECONDS = 25;
  const verifySecLeft =
    loading && verifyStartRef.current
      ? Math.max(0, VERIFY_SECONDS - Math.floor((nowTick - verifyStartRef.current) / 1000))
      : 0;
  const verifyPct =
    loading && verifyStartRef.current
      ? Math.min(100, Math.round(((VERIFY_SECONDS - verifySecLeft) / VERIFY_SECONDS) * 100))
      : 0;

  const title =
    step === "logout"
      ? "Шаг 1 · Выход из аккаунта"
      : step === "login"
      ? "Шаг 2 · Вход в выданный аккаунт"
      : revoked
      ? "Аккаунт отозван"
      : "Ваш аккаунт";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="shared-account-modal"
        className="bg-[#121721] border-white/10 text-white max-w-[440px] rounded-3xl max-h-[86vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
        </DialogHeader>

        {step === "logout" && (
          <div className="space-y-4" data-testid="step-logout">
            <video
              data-testid="guide-video"
              src={appName === "Сбербанк Онлайн" ? "/assets/guide/prosto_logout.mp4" : "/assets/guide/logout.mp4"}
              poster="/assets/guide/logout-poster.jpg"
              controls
              playsInline
              preload="none"
              className="w-full rounded-2xl border border-white/10 bg-black max-h-[300px] object-contain"
            />
            <p className="text-sm text-slate-300 leading-relaxed">
              Сначала выйдите из своего Apple ID: откройте «Настройки» → ваше имя вверху → пролистайте вниз и нажмите
              «Выйти». Как это сделать — показано на видео.
            </p>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-center">
              <div className="text-sm font-semibold mb-3">Вы вышли из аккаунта?</div>
              <button
                data-testid="confirm-logged-out"
                onClick={() => setStep("login")}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-black bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 active:scale-95 transition"
              >
                Да, я вышел(ла) <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {step === "login" && (
          <div className="space-y-4" data-testid="step-login">
            <p className="text-sm text-slate-300 leading-relaxed">
              Теперь откройте «Настройки» → «Аккаунт Apple» → «Войти вручную». Данные для входа вы получите на следующем
              шаге.
            </p>
            <div className="flex gap-3 justify-center">
              <GuideShot
                img={SHOT.settings.img}
                box={SHOT.settings.box}
                caption="Нажмите «Аккаунт Apple»"
                width={132}
                testId="shot-settings"
              />
              <GuideShot
                img={SHOT.manual.img}
                box={SHOT.manual.box}
                caption="Выберите «Войти вручную»"
                width={132}
                testId="shot-manual"
              />
            </div>
            <button
              data-testid="confirm-get-account"
              onClick={startIssue}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-black bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 active:scale-95 transition shadow-xl shadow-emerald-500/20"
            >
              <Download size={18} /> Получить данные
            </button>
          </div>
        )}

        {step === "account" && (
          <>
            {loading && (
              <div className="py-10 grid place-items-center text-center" data-testid="account-verifying">
                <div className="relative mb-5 w-28 h-28" data-testid="verify-countdown">
                  <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(16,185,129,0.15)" strokeWidth="8" />
                    <circle
                      cx="50"
                      cy="50"
                      r="44"
                      fill="none"
                      stroke="url(#verifyGrad)"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 44}
                      strokeDashoffset={(2 * Math.PI * 44 * (100 - verifyPct)) / 100}
                      style={{ transition: "stroke-dashoffset 1s linear" }}
                    />
                    <defs>
                      <linearGradient id="verifyGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#34d399" />
                        <stop offset="100%" stopColor="#22d3ee" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 grid place-items-center">
                    {verifySecLeft > 0 ? (
                      <div className="flex flex-col items-center leading-none">
                        <span
                          data-testid="verify-countdown-value"
                          className="text-4xl font-mono font-bold text-white tabular-nums"
                        >
                          {verifySecLeft}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-emerald-300/80 mt-1">сек</span>
                      </div>
                    ) : (
                      <Loader2 className="animate-spin text-emerald-400" size={38} />
                    )}
                  </div>
                </div>
                <div className="text-sm font-semibold text-white mb-1">🔍 Проверяем аккаунт…</div>
                <div className="text-xs text-slate-400">
                  {verifySecLeft > 0 ? (
                    <>
                      Входим в аккаунт и проверяем работоспособность.
                      <br />
                      Пожалуйста, подождите <b className="text-emerald-300">{verifySecLeft} сек</b> — не закрывайте
                      окно.
                    </>
                  ) : (
                    <>Почти готово, ещё пару секунд…</>
                  )}
                </div>
              </div>
            )}

            {!loading && error && (
              <div className="py-6 text-center space-y-4" data-testid="account-error" role="alert">
                <div className="text-amber-300 text-sm">{error}</div>
                <button
                  data-testid="retry-get-account"
                  onClick={startIssue}
                  className="px-5 py-2.5 rounded-2xl font-semibold text-black bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 active:scale-95 transition"
                >
                  Попробовать снова
                </button>
              </div>
            )}

            {!loading && revoked && (
              <div className="py-6 text-center space-y-5" data-testid="account-revoked">
                <div className="grid place-items-center">
                  <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 grid place-items-center">
                    <XCircle className="text-red-400" size={36} />
                  </div>
                </div>
                <div>
                  <div className="text-base font-bold text-white mb-1">Аккаунт отозван</div>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    {revoked.voluntary
                      ? "Запрос на отзыв отправлен на проверку. После подтверждения данные для входа будут отключены."
                      : "Срок выдачи истёк. Запрос передан на проверку; данные для входа могут оставаться активными до обработки."}
                  </p>
                </div>
                {reissueMs > 0 ? (
                  <div
                    data-testid="reissue-wait"
                    className="rounded-2xl bg-white/5 border border-white/10 p-4 text-sm text-slate-300"
                  >
                    Новый аккаунт можно запросить через{" "}
                    <span className="font-mono font-bold text-white">{fmt(reissueMs)}</span>
                  </div>
                ) : (
                  <button
                    data-testid="reissue-account"
                    onClick={startIssue}
                    className="px-5 py-2.5 rounded-2xl font-semibold text-black bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 active:scale-95 transition"
                  >
                    Запросить снова
                  </button>
                )}
              </div>
            )}

            {!loading && !revoked && acc && (
              <div className="space-y-4" data-testid="account-data">
                <Countdown remainingMs={remaining} />
                <CopyField label="Apple ID" value={acc.apple_id} testId="copy-apple-id-button" />
                <CopyField label="Пароль" value={acc.password} testId="copy-password-button" />
                {acc.note && (
                  <div className="flex gap-2 items-start text-xs text-amber-200 bg-amber-500/10 border border-amber-500/25 rounded-2xl p-3">
                    <ShieldAlert size={16} className="shrink-0 mt-0.5" />
                    <span>{acc.note}</span>
                  </div>
                )}

                <div className="rounded-2xl bg-blue-500/10 border border-blue-500/25 p-4 space-y-5" data-testid="other-options-info">
                  <div className="text-sm font-semibold text-blue-100">
                    Введите эти данные и на следующих экранах выбирайте так:
                  </div>
                  <div className="flex gap-3 justify-center items-start">
                    <GuideShot
                      img={SHOT.other.img}
                      box={SHOT.other.box}
                      caption="Нажмите «Другие возможности»"
                      width={128}
                      testId="shot-other"
                    />
                    <GuideShot
                      img={SHOT.dont.img}
                      box={SHOT.dont.box}
                      caption="Затем «Не улучшать»"
                      width={128}
                      testId="shot-dont"
                    />
                  </div>
                </div>

                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3" data-testid="purchases-info">
                  <div className="text-sm font-semibold">Подтвердите вход и скачайте приложение</div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Нажмите «Продолжить» (ничего не удалится — это стандартное предупреждение Apple), затем откройте App
                    Store → ваш профиль → «Покупки» / «Не на этом iPhone» и нажмите на облачко рядом с нужным приложением.
                  </p>
                  <div className="flex gap-2 justify-center items-start flex-wrap">
                    <GuideShot
                      img={SHOT.cont.img}
                      box={SHOT.cont.box}
                      caption="Нажмите «Продолжить»"
                      width={104}
                      testId="shot-continue"
                    />
                    <PurchasesShot highlightName={appName} width={184} />
                  </div>
                </div>

                <div className="text-xs text-slate-400 leading-relaxed">
                  Войдите с этими данными только в <b className="text-white">App Store</b>, не в iCloud. Не меняйте
                  пароль и не подключайте «Локатор».
                </div>

                {!confirmOpen ? (
                  <button
                    data-testid="revoke-account-button"
                    onClick={() => setConfirmOpen(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl font-semibold text-red-300 bg-transparent border border-red-500/40 hover:bg-red-500/10 active:scale-95 transition"
                  >
                    <Ban size={16} /> Отменить установку / Отозвать аккаунт
                  </button>
                ) : (
                  <div
                    data-testid="revoke-confirm"
                    className="rounded-2xl bg-red-500/10 border border-red-500/30 p-4 space-y-3"
                  >
                    <div className="text-sm text-red-100 leading-relaxed">
                      Это отправит запрос на отзыв аккаунта для проверки. Пароль автоматически не меняется. Продолжить?
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid="revoke-confirm-yes"
                        onClick={doRevoke}
                        disabled={revoking}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-white bg-red-500/90 hover:bg-red-500 active:scale-95 transition disabled:opacity-60"
                      >
                        {revoking ? <Loader2 size={16} className="animate-spin" /> : null} Да, отозвать
                      </button>
                      <button
                        data-testid="revoke-confirm-no"
                        onClick={() => setConfirmOpen(false)}
                        disabled={revoking}
                        className="flex-1 py-2.5 rounded-xl font-semibold text-slate-200 bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition"
                      >
                        Нет, оставить
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
